import React, { useState, useEffect } from 'react';
import { Car, MessageSquare, X, Target } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ProspectionFormFromMarket = ({ isOpen, onClose, onSubmit, carData }) => {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    currency_id: 'USD',
    price: '',
    kilometers: '',
    vehicle_year: '',
    description: '',
    city: '',
    state: '',
    vehicle_id: '',
    permalink: '',
    image: '',
    status: 'prospected',
    lead_type: '',
    phone: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Pre-llenar el formulario con los datos del vehículo cuando se abre
  useEffect(() => {
    if (isOpen && carData) {
      // Generar permalink si no existe
      let permalink = carData.permalink || '';
      if (!permalink && carData.id) {
        // Generar un permalink básico usando el ID del vehículo
        permalink = `https://auto.mercadolibre.com.ar/MLU-${carData.id}`;
      }

      setFormData({
        title: carData.title || '',
        currency_id: carData.currency_id || 'USD',
        price: carData.price ? String(carData.price) : '',
        kilometers: carData.kilometers ? String(carData.kilometers) : '',
        vehicle_year: carData.vehicle_year ? String(carData.vehicle_year) : '',
        description: carData.description || '',
        city: carData.city || '',
        state: carData.state || '',
        vehicle_id: carData.id ? String(carData.id) : '',
        permalink: permalink,
        image: carData.pictures || '', // Agregar la imagen del vehículo
        status: 'prospected',
        lead_type: '',
        phone: ''
      });
    }
  }, [isOpen, carData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'El título del vehículo es requerido';
    }
    
    if (!formData.permalink.trim()) {
      newErrors.permalink = 'El link del vehículo es requerido';
    } else {
      // Validar que sea una URL válida
      try {
        new URL(formData.permalink);
      } catch {
        newErrors.permalink = 'Debe ser un link válido (ej: https://auto.mercadolibre.com.ar/...)';
      }
    }
    
    if (formData.price && (isNaN(formData.price) || formData.price < 0)) {
      newErrors.price = 'El precio debe ser un número válido';
    }
    
    if (formData.vehicle_year && (isNaN(formData.vehicle_year) || formData.vehicle_year < 1900 || formData.vehicle_year > new Date().getFullYear())) {
      newErrors.vehicle_year = 'El año debe ser válido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Incluir user_id explícitamente si está disponible en el perfil
      const dataToSubmit = {
        ...formData,
        user_email: userProfile?.email,
        user_id: userProfile?.id
      };
      
      await onSubmit(dataToSubmit);
      
      // Resetear formulario
      setFormData({
        title: '',
        currency_id: 'USD',
        price: '',
        kilometers: '',
        vehicle_year: '',
        description: '',
        city: '',
        state: '',
        vehicle_id: '',
        permalink: '',
        image: '',
        status: 'prospected',
        lead_type: '',
        phone: ''
      });
      
      onClose();
    } catch (error) {
      console.error('Error al enviar prospección:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1c1c1a] rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#48a877]/15 rounded-lg">
              <Target className="w-5 h-5 text-[#48a877]" />
            </div>
            <h2 className="text-xl font-bold text-white">Prospectar Vehículo</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Vista previa de la imagen */}
          {formData.image && (
            <div className="flex justify-center">
              <div className="w-full max-w-xs">
                <img
                  src={formData.image}
                  alt={formData.title}
                  className="w-full h-32 object-cover rounded-lg border border-gray-700"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Car className="w-4 h-4 inline mr-2" />
              Título del Vehículo *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Ej: Toyota Corolla 2020 - Excelente Estado"
              className={`w-full px-4 py-3 bg-dark-bg border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-200 ${
                errors.title ? 'border-red-500' : 'border-gray-700'
              }`}
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-400">{errors.title}</p>
            )}
          </div>

          {/* Permalink */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Car className="w-4 h-4 inline mr-2" />
              Link del Vehículo *
            </label>
            <input
              type="url"
              name="permalink"
              value={formData.permalink}
              onChange={handleChange}
              placeholder="Ej: https://auto.mercadolibre.com.ar/MLU-123456789-..."
              className={`w-full px-4 py-3 bg-dark-bg border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-200 ${
                errors.permalink ? 'border-red-500' : 'border-gray-700'
              }`}
            />
            {errors.permalink && (
              <p className="mt-1 text-sm text-red-400">{errors.permalink}</p>
            )}
          </div>

          {/* Price and Currency */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Precio
              </label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="25000"
                className={`w-full px-4 py-3 bg-dark-bg border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-200 ${
                  errors.price ? 'border-red-500' : 'border-gray-700'
                }`}
              />
              {errors.price && (
                <p className="mt-1 text-sm text-red-400">{errors.price}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Moneda
              </label>
              <select
                name="currency_id"
                value={formData.currency_id}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-dark-bg border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
              >
                <option value="USD">USD</option>
                <option value="ARS">ARS</option>
              </select>
            </div>
          </div>

          {/* Kilometers and Year */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Kilómetros
              </label>
              <input
                type="text"
                name="kilometers"
                value={formData.kilometers}
                onChange={handleChange}
                placeholder="50000"
                className="w-full px-4 py-3 bg-dark-bg border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-200"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Año
              </label>
              <input
                type="number"
                name="vehicle_year"
                value={formData.vehicle_year}
                onChange={handleChange}
                placeholder="2020"
                min="1900"
                max={new Date().getFullYear()}
                className={`w-full px-4 py-3 bg-dark-bg border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-200 ${
                  errors.vehicle_year ? 'border-red-500' : 'border-gray-700'
                }`}
              />
              {errors.vehicle_year && (
                <p className="mt-1 text-sm text-red-400">{errors.vehicle_year}</p>
              )}
            </div>
          </div>

          {/* City and State */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Ciudad
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Buenos Aires"
                className="w-full px-4 py-3 bg-dark-bg border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-200"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Provincia/Estado
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="CABA"
                className="w-full px-4 py-3 bg-dark-bg border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-200"
              />
            </div>
          </div>

          {/* Vehicle ID */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              ID del Vehículo
            </label>
            <input
              type="text"
              name="vehicle_id"
              value={formData.vehicle_id}
              onChange={handleChange}
              placeholder="MLU123456789"
              className="w-full px-4 py-3 bg-dark-bg border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-200"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <MessageSquare className="w-4 h-4 inline mr-2" />
              Descripción
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Descripción detallada del vehículo, estado, características..."
              rows={4}
              className="w-full px-4 py-3 bg-dark-bg border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-200 resize-none"
            />
          </div>

          {/* Telefono */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Teléfono
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Ej: +598 99 123 456"
              className="w-full px-4 py-3 bg-dark-bg border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-200"
            />
          </div>

          {/* Lead type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Tipo de Lead
            </label>
            <div className="grid grid-cols-2 gap-3">
              {/* Good */}
              <label
                className={`cursor-pointer select-none rounded-xl border p-4 flex items-center justify-center gap-2 transition-all duration-200 
                  ${formData.lead_type === 'good'
                    ? 'border-green-500 bg-green-500/10 shadow-[0_0_0_2px_rgba(34,197,94,0.2)]'
                    : 'border-gray-700 hover:border-green-600/60 hover:bg-green-500/5'}`}
              >
                <input
                  type="radio"
                  name="lead_type"
                  value="good"
                  checked={formData.lead_type === 'good'}
                  onChange={handleChange}
                  className="hidden"
                />
                <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border 
                  ${formData.lead_type === 'good' ? 'border-green-400 bg-green-500/30' : 'border-gray-600'}`}
                >
                  {formData.lead_type === 'good' && (
                    <span className="h-2.5 w-2.5 rounded-full bg-green-400"></span>
                  )}
                </span>
                <span className="font-medium text-white">Good</span>
              </label>

              {/* Bad */}
              <label
                className={`cursor-pointer select-none rounded-xl border p-4 flex items-center justify-center gap-2 transition-all duration-200 
                  ${formData.lead_type === 'bad'
                    ? 'border-red-500 bg-red-500/10 shadow-[0_0_0_2px_rgba(239,68,68,0.2)]'
                    : 'border-gray-700 hover:border-red-600/60 hover:bg-red-500/5'}`}
              >
                <input
                  type="radio"
                  name="lead_type"
                  value="bad"
                  checked={formData.lead_type === 'bad'}
                  onChange={handleChange}
                  className="hidden"
                />
                <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border 
                  ${formData.lead_type === 'bad' ? 'border-red-400 bg-red-500/30' : 'border-gray-600'}`}
                >
                  {formData.lead_type === 'bad' && (
                    <span className="h-2.5 w-2.5 rounded-full bg-red-400"></span>
                  )}
                </span>
                <span className="font-medium text-white">Bad</span>
              </label>
            </div>
            {/* Clear selection */}
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, lead_type: '' }))}
              className="mt-2 text-xs text-gray-400 hover:text-gray-300 underline decoration-dotted"
            >
              Limpiar selección
            </button>
          </div>

          {/* Submit Button */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors duration-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Guardando...</span>
                </>
              ) : (
                <>
                  <Target className="w-4 h-4" />
                  <span>Prospectar</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProspectionFormFromMarket;